import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Privacy from "@/pages/Privacy";
import Unsubscribe from "@/pages/Unsubscribe";
import AdminEmailTemplates from "@/pages/AdminEmailTemplates";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import TeamSetup from "@/pages/TeamSetup";
import Dashboard from "@/pages/Dashboard";
import Programme from "@/pages/Programme";
import DailyWorkbook from "@/pages/DailyWorkbook";
import DayDetail from "@/pages/DayDetail";
import AdminDemoReset from "@/pages/AdminDemoReset";
import AdminManageAdmins from "@/pages/AdminManageAdmins";
import AdminPreviewPlatform from "@/pages/AdminPreviewPlatform";
import ProgrammeUploader from "@/pages/ProgrammeUploader";
import Retrospective from "@/pages/Retrospective";
import Journal from "@/pages/Journal";
import Library from "@/pages/Library";
import Resources from "@/pages/Resources";
import MyTeam from "@/pages/MyTeam";
import TeamsAdmin from "@/pages/TeamsAdmin";
import SurveyPage from "@/pages/SurveyPage";
import Frameworks from "@/pages/Frameworks";
import Mentors from "@/pages/Mentors";
import MentorBooking from "@/pages/MentorBooking";
import MentorDashboard from "@/pages/MentorDashboard";
import MentorTeamsProgress from "@/pages/MentorTeamsProgress";
import MentorTeamNotes from "@/pages/MentorTeamNotes";
import Speakers from "@/pages/Speakers";
import Roster from "@/pages/Roster";
import FinalPitch from "@/pages/FinalPitch";
import AdminFinalPitches from "@/pages/AdminFinalPitches";
import AdminTeamQuestions from "@/pages/AdminTeamQuestions";
// iTeams-only pages
import Sponsors from "@/pages/Sponsors";
import MySponsor from "@/pages/MySponsor";
import Tasks from "@/pages/Tasks";
import TeamReleaseGate from "@/components/TeamReleaseGate";
import AdminSurveyBoard from "@/pages/AdminSurveyBoard";
import AdminEmailLog from "@/pages/AdminEmailLog";
import MentorReports from "@/pages/MentorReports";
import Canvas from "@/pages/Canvas";
import MissionLock from "@/pages/MissionLock";
import Attendance from "@/pages/Attendance";

import { ConfirmProvider } from "@/context/ConfirmContext";
import RouteTitle from "@/components/RouteTitle";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ConfirmProvider>
            <RouteTitle />
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/forgot" element={<ForgotPassword />} />
              <Route path="/reset" element={<ResetPassword />} />
              <Route path="/team-setup" element={<ProtectedRoute><TeamSetup /></ProtectedRoute>} />

              <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/programme" element={<Programme />} />
                <Route path="/workbook" element={<DailyWorkbook />} />
                <Route path="/workbook/:id" element={<DayDetail />} />
                <Route path="/my-team" element={<TeamReleaseGate><MyTeam /></TeamReleaseGate>} />
                <Route path="/my-sponsor" element={<TeamReleaseGate><MySponsor /></TeamReleaseGate>} />
                <Route path="/tasks" element={<TeamReleaseGate><Tasks /></TeamReleaseGate>} />
                <Route path="/mentors" element={<Mentors />} />
                <Route path="/mentor-booking" element={<TeamReleaseGate><MentorBooking /></TeamReleaseGate>} />
                <Route path="/mentor/dashboard" element={<MentorDashboard />} />
                <Route path="/mentor/reports" element={<MentorReports />} />
                <Route path="/mentor/teams-progress" element={<MentorTeamsProgress />} />
                <Route path="/mentor/team-notes" element={<MentorTeamNotes />} />
                <Route path="/speakers" element={<Speakers />} />
                <Route path="/frameworks" element={<Frameworks />} />
                <Route path="/journal" element={<Journal />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/canvas/:slug" element={<Canvas />} />
                <Route path="/lens/mission-lock" element={<MissionLock />} />
                <Route path="/library" element={<Library />} />
                <Route path="/final-pitch" element={<FinalPitch />} />
                <Route path="/survey/opening" element={<SurveyPage kind="opening" />} />
                <Route path="/survey/exit" element={<SurveyPage kind="exit" />} />
                <Route path="/retrospective" element={<Retrospective />} />

                {/* Admin-only */}
                <Route path="/teams" element={<ProtectedRoute roles={["admin", "faculty"]}><TeamsAdmin /></ProtectedRoute>} />
                <Route path="/admin/sponsors" element={<ProtectedRoute roles={["admin", "faculty"]}><Sponsors /></ProtectedRoute>} />
                <Route path="/admin/mentor-reports" element={<ProtectedRoute roles={["admin", "faculty"]}><MentorReports /></ProtectedRoute>} />
                <Route path="/admin/final-pitches" element={<ProtectedRoute roles={["admin", "faculty"]}><AdminFinalPitches /></ProtectedRoute>} />
                <Route path="/admin/team-questions" element={<ProtectedRoute roles={["admin", "faculty"]}><AdminTeamQuestions /></ProtectedRoute>} />
                <Route path="/admin/surveys" element={<ProtectedRoute roles={["admin", "faculty"]}><AdminSurveyBoard /></ProtectedRoute>} />
                <Route path="/admin/email-log" element={<ProtectedRoute roles={["admin", "faculty"]}><AdminEmailLog /></ProtectedRoute>} />
                <Route path="/admin/email-templates" element={<ProtectedRoute roles={["admin", "faculty"]}><AdminEmailTemplates /></ProtectedRoute>} />
                <Route path="/admin/uploader" element={<ProtectedRoute roles={["admin", "faculty"]}><ProgrammeUploader /></ProtectedRoute>} />
                <Route path="/admin/admins" element={<ProtectedRoute roles={["admin", "faculty"]}><AdminManageAdmins /></ProtectedRoute>} />
                <Route path="/admin/preview-platform" element={<ProtectedRoute roles={["admin", "faculty"]}><AdminPreviewPlatform /></ProtectedRoute>} />
                <Route path="/admin/demo-reset" element={<ProtectedRoute roles={["admin", "faculty"]}><AdminDemoReset /></ProtectedRoute>} />
                <Route path="/roster" element={<ProtectedRoute roles={["admin", "faculty"]}><Roster /></ProtectedRoute>} />
                <Route path="/attendance" element={<ProtectedRoute roles={["admin", "faculty"]}><Attendance /></ProtectedRoute>} />
              </Route>

              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            <Toaster richColors position="top-right" />
          </ConfirmProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
